import { getDocs, collection, doc, addDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from '../../../../firebase/config';
import {
    designationIsIncharge,
    designationIsMisIncharge,
} from '../../../../utils/designationRole';
import { fetchCompanyBankId } from '../../../companies/service/companiesService';
import { fetchCompaniesData } from '../../../companies/service/companiesService';
import type { Company } from '../../../companies/service/companiesService';
import { createDeployment, deleteDeployment } from '../../../deployments/service/deploymentsService';

export type Branch = {
    id: string;
    bankId: string;
    branchCode?: string;
    address?: string;
    psoId?: string;
    psoName?: string;
    psoEmail?: string;
    psoContact?: string;
    psoDesignation?: string;
    locationId?: string;
    locationName?: string;
    [key: string]: any;
};

export type DeploymentRequest = {
    id: string;
    requestId?: string;
    bankId: string;
    branchId: string;
    
    // Renovated Branch Details
    coveredArea: number | string;
    renovationStartDate: string;
    renovationCompletionDate: string;
    requestDate: string;
    
    // Vendors Selected (Single of each type)
    cctvCompanyId: string | null;
    bamsCompanyId: string | null;
    guardCompanyId: string | null;
    bamsCompanyIds?: string[]; // Kept for compatibility
    
    // Remarks & Auditing
    reason: string; // Remarks of allocation of vendor with Vendor KPI
    cctvKpiRemarks?: string;
    bamsKpiRemarks?: string;
    guardKpiRemarks?: string;
    regionalHeadRemarks?: string;
    headSecurityRemarks?: string;
    rejectReason?: string;
    
    psoId?: string;
    createdBy?: string;
    createdAt?: any;
    updatedAt?: any;
    status?: 'Pending' | 'Approved' | 'Rejected' | 'Cancelled' | 'Forwarded to Head';
    
    // Enriched Details
    branch?: Branch | null;
    cctvCompany?: Company | null;
    bamsCompany?: Company | null;
    bamsCompanies?: Company[]; // Kept for compatibility
    guardCompany?: Company | null;
    
    forwardedAt?: any;
    forwardedBy?: string;
    forwardedByName?: string;
    rejectedAt?: any;
    rejectedBy?: string;
    rejectedByName?: string;
    rejectionStage?: 'Regional' | 'Head';
    approvedAt?: any;
    approvedBy?: string;
    approvedByName?: string;
    inchargeRead?: boolean;
};

/**
 * Fetch bankId for a user (PSO or Bank)
 */
export async function fetchDeploymentRequestBankId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    return await fetchCompanyBankId(uid, isBankUser);
}

/**
 * Fetch branches for a bank, optionally filtered by PSO ID
 */
export async function fetchBranches(bankId: string, psoId?: string): Promise<Branch[]> {
    try {
        const branchSnap = await getDocs(collection(db, "banks", bankId, "branch"));
        let branches = branchSnap.docs.map(doc => ({
            id: doc.id,
            bankId,
            ...doc.data()
        })) as Branch[];

        // Fetch locations to get region names
        const locSnap = await getDocs(collection(db, "locations"));
        const locMap = new Map(locSnap.docs.map(d => [d.id, d.data().name]));

        // Fetch PSOs to enrich branch data
        const psoSnap = await getDocs(collection(db, "banks", bankId, "pos"));
        const psoList = psoSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));

        branches = branches.map(branch => {
            const pso = branch.psoId ? psoList.find((p: any) => p.id === branch.psoId) : null;
            const locationName = branch.locationId ? locMap.get(branch.locationId) : null;

            return {
                ...branch,
                locationName: locationName || branch.locationName || "N/A",
                psoName: pso?.name || branch.psoName,
                psoEmail: pso?.email || branch.psoEmail,
                psoContact: pso?.contact || branch.psoContact,
                psoDesignation: pso?.designation || branch.psoDesignation
            };
        });

        if (psoId) {
            branches = branches.filter(branch => branch.psoId === psoId);
        }

        return branches;
    } catch (error) {
        console.error('Error fetching branches:', error);
        throw error;
    }
}

/**
 * Calculate the share of active deployments for a specific vendor in a region
 */
export async function calculateRegionalVendorShare(
    bankId: string,
    locationId: string,
    companyId: string
): Promise<{ allocatedCount: number; totalBranches: number; percentage: number }> {
    if (!bankId || !locationId || !companyId) {
        return { allocatedCount: 0, totalBranches: 0, percentage: 0 };
    }
    try {
        // 1. Get all branches in this region
        const branchesSnap = await getDocs(collection(db, "banks", bankId, "branch"));
        const regionBranches = branchesSnap.docs
            .map(d => ({ id: d.id, ...d.data() } as any))
            .filter(b => b.locationId === locationId);
        
        const totalBranches = regionBranches.length;
        if (totalBranches === 0) {
            return { allocatedCount: 0, totalBranches: 0, percentage: 0 };
        }

        // 2. Get all deployments for this vendor on these regional branches
        const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
        const activeDeployments = deploymentsSnap.docs.map(doc => doc.data() as any);
        
        const allocatedCount = activeDeployments.filter(d => 
            d.companiesId === companyId && 
            regionBranches.some(rb => rb.id === d.branchId)
        ).length;

        const percentage = Math.round((allocatedCount / totalBranches) * 100);
        return { allocatedCount, totalBranches, percentage };
    } catch (error) {
        console.error("Error calculating regional vendor share:", error);
        return { allocatedCount: 0, totalBranches: 0, percentage: 0 };
    }
}

/**
 * Fetch deployment requests for a bank, filtered by roles and designation
 */
export async function fetchDeploymentRequestsData(
    uid: string,
    isBankUser: boolean = false,
    isRegionalHead: boolean = false,
    psoDesignation?: string,
    isHead: boolean = false
): Promise<DeploymentRequest[]> {
    const bankId = await fetchDeploymentRequestBankId(uid, isBankUser);
    if (!bankId) {
        return [];
    }

    try {
        const requestsSnap = await getDocs(collection(db, "banks", bankId, "deploymentRequest"));
        let requests = requestsSnap.docs.map(doc => ({
            id: doc.id,
            bankId,
            ...doc.data()
        })) as DeploymentRequest[];

        const isIncharge = designationIsIncharge(psoDesignation);
        const inchargeType = psoDesignation?.replace(/_/g, ' ').toLowerCase() || '';
        const isInchargeCCTV = isIncharge && inchargeType.includes('cctv');
        const isInchargeBAMS = isIncharge && inchargeType.includes('bams');
        const isInchargeGuarding = isIncharge && inchargeType.includes('guard');

        // Standard filter for base PSO user
        if (!isBankUser && !isRegionalHead && !isHead && !isIncharge) {
            requests = requests.filter(req => req.psoId === uid);
        }

        // Fetch supporting data
        const branches = await fetchBranches(bankId);
        const companies = await fetchCompaniesData(uid, isBankUser);

        // Enrich requests
        let enrichedRequests = requests.map(req => {
            const branch = branches.find(b => b.id === req.branchId);
            const cctvCompany = req.cctvCompanyId ? companies.find(c => c.id === req.cctvCompanyId) : null;
            
            // Handle single BAMS mapping
            const singleBamsId = req.bamsCompanyId || (req.bamsCompanyIds && req.bamsCompanyIds[0]) || null;
            const bamsCompany = singleBamsId ? companies.find(c => c.id === singleBamsId) : null;
            
            const guardCompany = req.guardCompanyId ? companies.find(c => c.id === req.guardCompanyId) : null;

            return {
                ...req,
                branch: branch || null,
                cctvCompany: cctvCompany || null,
                bamsCompany: bamsCompany || null,
                bamsCompanies: bamsCompany ? [bamsCompany] : [], // Compatibility array
                guardCompany: guardCompany || null,
                status: req.status || 'Pending'
            };
        });

        // Filter for incharge users (only see approved requests relevant to their tech)
        if (isIncharge) {
            enrichedRequests = enrichedRequests.filter(req => req.status === 'Approved');

            if (isInchargeCCTV) {
                enrichedRequests = enrichedRequests.filter(req => !!req.cctvCompanyId && req.cctvCompanyId !== 'none');
            } else if (isInchargeBAMS) {
                enrichedRequests = enrichedRequests.filter(req => !!req.bamsCompanyId && req.bamsCompanyId !== 'none');
            } else if (isInchargeGuarding) {
                enrichedRequests = enrichedRequests.filter(req => !!req.guardCompanyId && req.guardCompanyId !== 'none');
            }
        }

        // Sort by createdAt descending
        enrichedRequests.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt ? new Date(a.createdAt) : new Date(0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt ? new Date(b.createdAt) : new Date(0);
            return dateB.getTime() - dateA.getTime();
        });

        return enrichedRequests;
    } catch (error) {
        console.error('Error fetching deployment requests:', error);
        return [];
    }
}

/**
 * Generate a unique deployment request ID: DR-001-xxxxxxx
 */
export async function generateRequestId(bankId: string): Promise<string> {
    try {
        const requestsSnap = await getDocs(collection(db, "banks", bankId, "deploymentRequest"));
        let maxNumber = 0;
        requestsSnap.docs.forEach(doc => {
            const data = doc.data();
            if (data.requestId && data.requestId.startsWith('DR-')) {
                const parts = data.requestId.split('-');
                if (parts.length >= 3) {
                    const number = parseInt(parts[1]);
                    if (!isNaN(number) && number > maxNumber) {
                        maxNumber = number;
                    }
                }
            }
        });

        const nextNumber = maxNumber + 1;
        const timestamp = Date.now().toString().slice(-7);
        return `DR-${nextNumber.toString().padStart(3, '0')}-${timestamp}`;
    } catch (error) {
        console.error('Error generating request ID:', error);
        const timestamp = Date.now().toString().slice(-7);
        return `DR-001-${timestamp}`;
    }
}

/**
 * Create a new deployment request
 */
export async function createDeploymentRequest(
    bankId: string,
    branchId: string,
    requestDate: string,
    coveredArea: number | string,
    renovationStartDate: string,
    renovationCompletionDate: string,
    cctvCompanyId: string | null,
    bamsCompanyId: string | null,
    guardCompanyId: string | null,
    psoId: string,
    reason: string,
    cctvKpiRemarks?: string,
    bamsKpiRemarks?: string,
    guardKpiRemarks?: string
): Promise<string> {
    try {
        const requestId = await generateRequestId(bankId);
        const requestData = {
            requestId,
            branchId,
            requestDate,
            coveredArea,
            renovationStartDate,
            renovationCompletionDate,
            cctvCompanyId,
            bamsCompanyId,
            guardCompanyId,
            bamsCompanyIds: bamsCompanyId ? [bamsCompanyId] : [], // compatibility
            psoId,
            createdBy: psoId,
            status: 'Pending',
            reason: reason || '',
            cctvKpiRemarks: cctvKpiRemarks || '',
            bamsKpiRemarks: bamsKpiRemarks || '',
            guardKpiRemarks: guardKpiRemarks || '',
            createdAt: new Date(),
            updatedAt: new Date()
        };

        const docRef = await addDoc(collection(db, "banks", bankId, "deploymentRequest"), requestData);
        return docRef.id;
    } catch (error) {
        console.error('Error creating deployment request:', error);
        throw error;
    }
}

/**
 * Update deployment request vendors and details
 */
export async function updateDeploymentRequestVendors(
    bankId: string,
    requestId: string,
    requestDate: string,
    coveredArea: number | string,
    renovationStartDate: string,
    renovationCompletionDate: string,
    cctvCompanyId: string | null,
    bamsCompanyId: string | null,
    guardCompanyId: string | null,
    reason: string,
    cctvKpiRemarks?: string,
    bamsKpiRemarks?: string,
    guardKpiRemarks?: string
): Promise<void> {
    try {
        const docRef = doc(db, "banks", bankId, "deploymentRequest", requestId);
        await updateDoc(docRef, {
            requestDate,
            coveredArea,
            renovationStartDate,
            renovationCompletionDate,
            cctvCompanyId,
            bamsCompanyId,
            bamsCompanyIds: bamsCompanyId ? [bamsCompanyId] : [], // compatibility
            guardCompanyId,
            reason,
            cctvKpiRemarks: cctvKpiRemarks || '',
            bamsKpiRemarks: bamsKpiRemarks || '',
            guardKpiRemarks: guardKpiRemarks || '',
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Error updating deployment request vendors:', error);
        throw error;
    }
}

/**
 * Automatically deploy companies to the branch upon Head approval.
 */
export async function deployCompaniesFromRequest(
    bankId: string,
    branchId: string,
    cctvCompanyId: string | null,
    bamsCompanyId: string | null,
    guardCompanyId: string | null,
    psoId: string
): Promise<void> {
    try {
        // Fetch all deployments for this branch
        const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
        const existingDeployments = deploymentsSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() } as any))
            .filter(d => d.branchId === branchId);

        // Fetch company data to identify company types
        const companiesSnap = await getDocs(collection(db, "banks", bankId, "companies"));
        const companiesList = companiesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));

        // 1. CCTV Company Deployment
        if (cctvCompanyId) {
            const existingCctv = existingDeployments.find(d => {
                const comp = companiesList.find(c => c.id === d.companiesId);
                return comp?.companyType === 'CCTV';
            });
            if (existingCctv) {
                if (existingCctv.companiesId !== cctvCompanyId) {
                    await deleteDoc(doc(db, "banks", bankId, "companyDeployment", existingCctv.id));
                    await createDeployment(bankId, branchId, cctvCompanyId, psoId);
                }
            } else {
                await createDeployment(bankId, branchId, cctvCompanyId, psoId);
            }
        }

        // 2. Guard Company Deployment
        if (guardCompanyId) {
            const existingGuard = existingDeployments.find(d => {
                const comp = companiesList.find(c => c.id === d.companiesId);
                return comp?.companyType === 'Guard Company' || comp?.companyType?.toLowerCase().includes('guard');
            });
            if (existingGuard) {
                if (existingGuard.companiesId !== guardCompanyId) {
                    await deleteDoc(doc(db, "banks", bankId, "companyDeployment", existingGuard.id));
                    await createDeployment(bankId, branchId, guardCompanyId, psoId);
                }
            } else {
                await createDeployment(bankId, branchId, guardCompanyId, psoId);
            }
        }

        // 3. BAMS Company Deployment
        if (bamsCompanyId) {
            const existingBams = existingDeployments.find(d => {
                const comp = companiesList.find(c => c.id === d.companiesId);
                return comp?.companyType === 'BAMS';
            });
            if (existingBams) {
                if (existingBams.companiesId !== bamsCompanyId) {
                    await deleteDoc(doc(db, "banks", bankId, "companyDeployment", existingBams.id));
                    await createDeployment(bankId, branchId, bamsCompanyId, psoId);
                }
            } else {
                await createDeployment(bankId, branchId, bamsCompanyId, psoId);
            }
        }
    } catch (error) {
        console.error('Error deploying companies from request:', error);
        throw error;
    }
}

/**
 * Update deployment request status
 */
export async function updateDeploymentRequestStatus(
    bankId: string,
    requestId: string,
    status: 'Pending' | 'Approved' | 'Rejected' | 'Cancelled' | 'Forwarded to Head',
    extra?: {
        rejectReason?: string;
        rejectedBy?: string;
        rejectedByName?: string;
        rejectionStage?: 'Regional' | 'Head';
        forwardedBy?: string;
        forwardedByName?: string;
        approvedBy?: string;
        approvedByName?: string;
        regionalHeadRemarks?: string;
        headSecurityRemarks?: string;
        // For auto-deployment trigger on approval
        branchId?: string;
        cctvCompanyId?: string | null;
        bamsCompanyId?: string | null;
        guardCompanyId?: string | null;
        psoId?: string;
    }
): Promise<void> {
    try {
        const docRef = doc(db, "banks", bankId, "deploymentRequest", requestId);
        const updateData: any = {
            status,
            updatedAt: new Date()
        };

        if (status === 'Approved') {
            updateData.approvedAt = new Date();
            if (extra?.approvedBy) updateData.approvedBy = extra.approvedBy;
            if (extra?.approvedByName) updateData.approvedByName = extra.approvedByName;
            if (extra?.headSecurityRemarks !== undefined) updateData.headSecurityRemarks = extra.headSecurityRemarks;

            // Trigger auto-deployment writing
            if (extra?.branchId && extra?.psoId) {
                await deployCompaniesFromRequest(
                    bankId,
                    extra.branchId,
                    extra.cctvCompanyId || null,
                    extra.bamsCompanyId || null,
                    extra.guardCompanyId || null,
                    extra.psoId
                );
            }
        } else if (status === 'Rejected') {
            updateData.rejectedAt = new Date();
            if (extra?.rejectReason !== undefined) updateData.rejectReason = extra.rejectReason;
            if (extra?.rejectedBy) updateData.rejectedBy = extra.rejectedBy;
            if (extra?.rejectedByName) updateData.rejectedByName = extra.rejectedByName;
            if (extra?.rejectionStage) updateData.rejectionStage = extra.rejectionStage;
            if (extra?.headSecurityRemarks !== undefined) updateData.headSecurityRemarks = extra.headSecurityRemarks;
        } else if (status === 'Forwarded to Head') {
            updateData.forwardedAt = new Date();
            if (extra?.forwardedBy) updateData.forwardedBy = extra.forwardedBy;
            if (extra?.forwardedByName) updateData.forwardedByName = extra.forwardedByName;
            if (extra?.regionalHeadRemarks !== undefined) updateData.regionalHeadRemarks = extra.regionalHeadRemarks;
            // Also store any updated vendors if the Regional Head edited them
            if (extra?.cctvCompanyId !== undefined) updateData.cctvCompanyId = extra.cctvCompanyId;
            if (extra?.bamsCompanyId !== undefined) updateData.bamsCompanyId = extra.bamsCompanyId;
            if (extra?.guardCompanyId !== undefined) updateData.guardCompanyId = extra.guardCompanyId;
            if (extra?.bamsCompanyId !== undefined) updateData.bamsCompanyIds = extra.bamsCompanyId ? [extra.bamsCompanyId] : [];
        } else if (status === 'Pending') {
            // Used during resubmission to clear past review states
            updateData.rejectReason = null;
            updateData.rejectDate = null;
            updateData.rejectionStage = null;
            updateData.rejectedBy = null;
            updateData.rejectedByName = null;
            updateData.forwardedBy = null;
            updateData.forwardedByName = null;
            updateData.forwardedAt = null;
            updateData.regionalHeadRemarks = null;
            updateData.headSecurityRemarks = null;
        }

        await updateDoc(docRef, updateData);
    } catch (error) {
        console.error('Error updating deployment request status:', error);
        throw error;
    }
}

/**
 * Delete a deployment request
 */
export async function deleteDeploymentRequest(bankId: string, requestId: string): Promise<void> {
    try {
        const docRef = doc(db, "banks", bankId, "deploymentRequest", requestId);
        await deleteDoc(docRef);
    } catch (error) {
        console.error('Error deleting deployment request:', error);
        throw error;
    }
}

/**
 * Update incharge read status for a deployment request
 */
export async function updateInchargeReadStatus(
    bankId: string,
    requestId: string,
    inchargeRead: boolean
): Promise<void> {
    try {
        const docRef = doc(db, "banks", bankId, "deploymentRequest", requestId);
        await updateDoc(docRef, {
            inchargeRead,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Error updating incharge read status:', error);
        throw error;
    }
}
